import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import LegalSeoJsonLd from './components/SeoJsonLd';

const sections = [
  { id: 'privacidad', label: 'Política de Privacidad', icon: 'ri-shield-check-line' },
  { id: 'recopilacion', label: 'Recopilación de Datos', icon: 'ri-database-2-line' },
  { id: 'uso-informacion', label: 'Uso de Información', icon: 'ri-file-list-3-line' },
  { id: 'cookies', label: 'Cookies', icon: 'ri-settings-4-line' },
  { id: 'derechos', label: 'Derechos del Usuario', icon: 'ri-user-settings-line' },
  { id: 'terminos', label: 'Términos de Servicio', icon: 'ri-file-text-line' },
  { id: 'licencias', label: 'Licencias de Software', icon: 'ri-key-2-line' },
  { id: 'pagos', label: 'Pagos y Reembolsos', icon: 'ri-money-dollar-circle-line' },
  { id: 'responsabilidad', label: 'Limitación de Responsabilidad', icon: 'ri-scales-3-line' },
  { id: 'modificaciones', label: 'Modificaciones', icon: 'ri-edit-line' },
  { id: 'contacto-legal', label: 'Contacto Legal', icon: 'ri-mail-send-line' },
];

export default function LegalPage() {
  const [activeSection, setActiveSection] = useState('privacidad');
  const [showMobileNav, setShowMobileNav] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-80px 0px -60% 0px' }
    );

    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setShowMobileNav(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <LegalSeoJsonLd />
      <Header />

      {/* Hero */}
      <section className="relative w-full h-[200px] sm:h-[260px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
          <img
            src="https://public.readdy.ai/ai/img_res/3a0708c26ff8e775187688f6d6ade54d.jpg"
            alt="Legal background"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-transparent to-slate-900/60"></div>
        </div>
        <div className="relative container mx-auto px-4 h-full flex flex-col justify-center items-center text-center">
          <div className="inline-flex items-center gap-2 bg-teal-500/15 border border-teal-400/30 text-teal-300 px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold uppercase tracking-wider mb-3">
            <i className="ri-file-shield-2-line"></i>
            Información Legal
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-2">
            Política de Privacidad y Términos
          </h1>
          <p className="text-gray-300 text-xs sm:text-sm max-w-xl">
            Última actualización: Enero 2025 &bull; Digital Licence Store
          </p>
        </div>
      </section>

      {/* Mobile Nav Toggle */}
      <div className="lg:hidden sticky top-[60px] z-30 bg-white border-b border-gray-200 px-4 py-3">
        <button
          onClick={() => setShowMobileNav(!showMobileNav)}
          className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <i className="ri-menu-2-line text-teal-600"></i>
            Navegación del documento
          </span>
          <i className={`ri-arrow-${showMobileNav ? 'up' : 'down'}-s-line text-gray-400`}></i>
        </button>
        {showMobileNav && (
          <nav className="mt-3 space-y-1 max-h-[50vh] overflow-y-auto">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                  activeSection === s.id
                    ? 'bg-teal-50 text-teal-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <i className={`${s.icon} text-base ${activeSection === s.id ? 'text-teal-600' : 'text-gray-400'}`}></i>
                {s.label}
              </button>
            ))}
          </nav>
        )}
      </div>

      {/* Content */}
      <main className="flex-1 container mx-auto px-4 py-8 lg:py-12">
        <div className="flex gap-10">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:block w-[260px] flex-shrink-0">
            <nav className="sticky top-[140px] space-y-1">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3">Contenido</p>
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer ${
                    activeSection === s.id
                      ? 'bg-teal-50 text-teal-700 font-semibold border-l-3 border-teal-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <i className={`${s.icon} text-base ${activeSection === s.id ? 'text-teal-600' : 'text-gray-400'}`}></i>
                  {s.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <div className="flex-1 max-w-3xl">
            {/* Política de Privacidad */}
            <article id="privacidad" className="mb-14 scroll-mt-[100px]">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-xl text-teal-600">
                  <i className="ri-shield-check-line text-xl"></i>
                </span>
                Política de Privacidad
              </h2>
              <div className="w-14 h-1 bg-teal-500 rounded-full mb-6"></div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                En <strong>Digital Licence Store</strong>, nos comprometemos a proteger la privacidad de nuestros usuarios. Esta política describe cómo recopilamos, usamos y protegemos tu información personal cuando utilizas nuestro sitio web y servicios de venta de licencias de software originales.
              </p>
              <div className="bg-teal-50 border-l-4 border-teal-500 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-teal-600 text-lg mt-0.5"></i>
                  <p className="text-sm text-teal-800 leading-relaxed">
                    Al utilizar nuestro sitio web, aceptas las prácticas descritas en esta política de privacidad. Te recomendamos leerla detenidamente antes de proporcionarnos cualquier dato personal.
                  </p>
                </div>
              </div>
            </article>

            {/* Recopilación de Datos */}
            <article id="recopilacion" className="mb-14 scroll-mt-[100px]">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-xl text-amber-600">
                  <i className="ri-database-2-line text-lg"></i>
                </span>
                Recopilación de Datos
              </h2>
              <div className="w-12 h-1 bg-amber-500 rounded-full mb-6"></div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Recopilamos información que nos proporcionas directamente al realizar una compra o contactarnos:
              </p>
              <ul className="space-y-3 mb-4">
                {[
                  'Nombre completo y dirección de correo electrónico',
                  'Información de pago procesada de forma segura a través de PayPal o MercadoPago',
                  'Dirección IP y datos de navegación para mejorar la experiencia del usuario',
                  'Historial de compras y licencias adquiridas',
                  'Información de contacto proporcionada a través de WhatsApp, Telegram o email',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                    <span className="w-5 h-5 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-amber-600 text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Datos recopilados automáticamente</h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Nuestro sitio web puede recopilar automáticamente información técnica como el tipo de navegador, sistema operativo, páginas visitadas y tiempo de permanencia. Esta información se utiliza exclusivamente para mejorar nuestros servicios y la experiencia del usuario.
                </p>
              </div>
            </article>

            {/* Uso de Información */}
            <article id="uso-informacion" className="mb-14 scroll-mt-[100px]">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-xl text-emerald-600">
                  <i className="ri-file-list-3-line text-lg"></i>
                </span>
                Uso de la Información
              </h2>
              <div className="w-12 h-1 bg-emerald-500 rounded-full mb-6"></div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Utilizamos la información recopilada para los siguientes fines:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {[
                  { icon: 'ri-shopping-bag-line', title: 'Procesar pedidos', desc: 'Gestionar y entregar tus licencias de software' },
                  { icon: 'ri-mail-send-line', title: 'Comunicaciones', desc: 'Enviar confirmaciones de compra y licencias por email' },
                  { icon: 'ri-customer-service-2-line', title: 'Soporte técnico', desc: 'Brindarte asistencia personalizada cuando lo necesites' },
                  { icon: 'ri-bar-chart-line', title: 'Mejoras del servicio', desc: 'Analizar y mejorar la experiencia en nuestro sitio' },
                ].map((item, i) => (
                  <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-2.5 mb-2">
                      <span className="w-7 h-7 flex items-center justify-center bg-emerald-50 rounded-lg">
                        <i className={`${item.icon} text-emerald-600 text-sm`}></i>
                      </span>
                      <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                <strong>No vendemos ni compartimos</strong> tu información personal con terceros para fines de marketing. Solo compartimos datos con procesadores de pago (PayPal, MercadoPago) estrictamente necesarios para completar tus transacciones.
              </p>
            </article>

            {/* Cookies */}
            <article id="cookies" className="mb-14 scroll-mt-[100px]">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-orange-100 rounded-xl text-orange-600">
                  <i className="ri-settings-4-line text-lg"></i>
                </span>
                Política de Cookies
              </h2>
              <div className="w-12 h-1 bg-orange-500 rounded-full mb-6"></div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Nuestro sitio web utiliza cookies y tecnologías similares para mejorar tu experiencia de navegación:
              </p>
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-4 py-3 font-semibold text-gray-700 text-xs">Tipo</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700 text-xs">Propósito</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700 text-xs">Duración</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { type: 'Esenciales', purpose: 'Funcionamiento del carrito de compras y sesión', duration: 'Sesión' },
                      { type: 'Preferencias', purpose: 'Recordar tu moneda preferida (COP/USD/EUR)', duration: '1 año' },
                      { type: 'Analíticas', purpose: 'Estadísticas anónimas de uso del sitio', duration: '2 años' },
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="px-4 py-3 text-gray-900 font-medium text-xs">{row.type}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{row.purpose}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{row.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">
                Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades del sitio como el carrito de compras.
              </p>
            </article>

            {/* Derechos del Usuario */}
            <article id="derechos" className="mb-14 scroll-mt-[100px]">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-cyan-100 rounded-xl text-cyan-600">
                  <i className="ri-user-settings-line text-lg"></i>
                </span>
                Derechos del Usuario
              </h2>
              <div className="w-12 h-1 bg-cyan-500 rounded-full mb-6"></div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Como usuario de Digital Licence Store, tienes los siguientes derechos sobre tus datos personales:
              </p>
              <div className="space-y-3 mb-4">
                {[
                  { title: 'Derecho de acceso', desc: 'Solicitar una copia de los datos personales que tenemos sobre ti.' },
                  { title: 'Derecho de rectificación', desc: 'Corregir cualquier dato personal inexacto o incompleto.' },
                  { title: 'Derecho de eliminación', desc: 'Solicitar la eliminación de tus datos personales de nuestros sistemas.' },
                  { title: 'Derecho de portabilidad', desc: 'Recibir tus datos en un formato estructurado y legible por máquina.' },
                  { title: 'Derecho de oposición', desc: 'Oponerte al procesamiento de tus datos para ciertos fines.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 bg-cyan-50/50 rounded-xl p-4 border border-cyan-100/50">
                    <span className="w-6 h-6 flex items-center justify-center bg-cyan-500 rounded-full flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs font-bold">{i + 1}</span>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-0.5">{item.title}</h3>
                      <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">
                Para ejercer cualquiera de estos derechos, contáctanos a través de <a href="mailto:DigitalLicenceStore@offersshopping.com" rel="nofollow" className="text-teal-600 hover:underline cursor-pointer">DigitalLicenceStore@offersshopping.com</a>.
              </p>
            </article>

            {/* Divider */}
            <div className="border-t-2 border-gray-100 my-14"></div>

            {/* Términos de Servicio */}
            <article id="terminos" className="mb-14 scroll-mt-[100px]">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl text-slate-600">
                  <i className="ri-file-text-line text-xl"></i>
                </span>
                Términos de Servicio
              </h2>
              <div className="w-14 h-1 bg-slate-500 rounded-full mb-6"></div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Al acceder y utilizar el sitio web de Digital Licence Store, aceptas cumplir con los siguientes términos y condiciones. Si no estás de acuerdo con alguno de estos términos, te pedimos que no utilices nuestros servicios.
              </p>
              <div className="bg-slate-50 border-l-4 border-slate-500 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-slate-600 text-lg mt-0.5"></i>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Estos términos constituyen un acuerdo legal vinculante entre tú y Digital Licence Store. Al realizar una compra, confirmas que has leído y aceptado estos términos en su totalidad.
                  </p>
                </div>
              </div>
            </article>

            {/* Licencias de Software */}
            <article id="licencias" className="mb-14 scroll-mt-[100px]">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-indigo-100 rounded-xl text-indigo-600">
                  <i className="ri-key-2-line text-lg"></i>
                </span>
                Licencias de Software
              </h2>
              <div className="w-12 h-1 bg-indigo-500 rounded-full mb-6"></div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Todas las licencias vendidas en Digital Licence Store son <strong>100% originales</strong> y están sujetas a los términos del fabricante correspondiente:
              </p>
              <ul className="space-y-3 mb-4">
                {[
                  'Las licencias son para uso personal o comercial según el tipo adquirido.',
                  'Cada licencia es válida para la cantidad de dispositivos especificada en la descripción del producto.',
                  'Las licencias perpetuas no tienen fecha de expiración y son válidas de por vida.',
                  'Las licencias de suscripción (como Microsoft 365) tienen una duración específica indicada en el producto.',
                  'La reventa de licencias adquiridas está prohibida sin autorización previa.',
                  'Las licencias se entregan por correo electrónico en formato digital.',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                    <span className="w-5 h-5 flex items-center justify-center bg-indigo-100 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-indigo-600 text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </article>

            {/* Pagos y Reembolsos */}
            <article id="pagos" className="mb-14 scroll-mt-[100px]">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-xl text-green-600">
                  <i className="ri-money-dollar-circle-line text-lg"></i>
                </span>
                Pagos y Reembolsos
              </h2>
              <div className="w-12 h-1 bg-green-500 rounded-full mb-6"></div>
              <div className="space-y-4 mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Métodos de Pago</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Aceptamos pagos a través de PayPal, MercadoPago, transferencia bancaria y criptomonedas. Todos los pagos son procesados de forma segura a través de plataformas certificadas.
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Precios</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Los precios se muestran en Pesos Colombianos (COP) con opción de visualización en USD y EUR. Los precios pueden variar según las tasas de cambio vigentes y están sujetos a cambios sin previo aviso.
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-green-800 mb-2">Política de Reembolsos</h3>
                  <ul className="space-y-2">
                    {[
                      'Si la licencia no funciona correctamente, ofrecemos reemplazo gratuito o reembolso completo.',
                      'Las solicitudes de reembolso deben realizarse dentro de los 30 días posteriores a la compra.',
                      'No se aceptan reembolsos si la licencia ya ha sido activada y funciona correctamente.',
                      'Los reembolsos se procesan por el mismo método de pago utilizado en la compra.',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-green-700">
                        <i className="ri-arrow-right-s-line text-green-500 mt-0.5"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>

            {/* Limitación de Responsabilidad */}
            <article id="responsabilidad" className="mb-14 scroll-mt-[100px]">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-rose-100 rounded-xl text-rose-600">
                  <i className="ri-scales-3-line text-lg"></i>
                </span>
                Limitación de Responsabilidad
              </h2>
              <div className="w-12 h-1 bg-rose-500 rounded-full mb-6"></div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Digital Licence Store se compromete a ofrecer productos de calidad, sin embargo:
              </p>
              <ul className="space-y-3 mb-4">
                {[
                  'No somos responsables por problemas de compatibilidad con hardware o software de terceros.',
                  'No garantizamos la disponibilidad ininterrumpida del sitio web.',
                  'La responsabilidad máxima se limita al valor de la compra realizada.',
                  'No somos responsables por el uso indebido de las licencias adquiridas.',
                  'Los tiempos de entrega pueden variar en casos excepcionales de alta demanda.',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                    <span className="w-5 h-5 flex items-center justify-center bg-rose-100 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-alert-line text-rose-500 text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </article>

            {/* Modificaciones */}
            <article id="modificaciones" className="mb-14 scroll-mt-[100px]">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-violet-100 rounded-xl text-violet-600">
                  <i className="ri-edit-line text-lg"></i>
                </span>
                Modificaciones
              </h2>
              <div className="w-12 h-1 bg-violet-500 rounded-full mb-6"></div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Nos reservamos el derecho de modificar esta política de privacidad y los términos de servicio en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación en el sitio web.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                Te recomendamos revisar periódicamente esta página para estar al tanto de cualquier actualización. El uso continuado de nuestros servicios después de la publicación de cambios constituye tu aceptación de los mismos.
              </p>
            </article>

            {/* Contacto Legal */}
            <article id="contacto-legal" className="mb-8 scroll-mt-[100px]">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-teal-100 rounded-xl text-teal-600">
                  <i className="ri-mail-send-line text-lg"></i>
                </span>
                Contacto Legal
              </h2>
              <div className="w-12 h-1 bg-teal-500 rounded-full mb-6"></div>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Si tienes preguntas sobre nuestra política de privacidad o términos de servicio, no dudes en contactarnos:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <a
                  href="mailto:DigitalLicenceStore@offersshopping.com"
                  rel="nofollow"
                  className="flex flex-col items-center gap-3 bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-100 rounded-xl p-5 hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl text-white group-hover:scale-110 transition-transform">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400 mb-0.5">Email</p>
                    <p className="text-xs font-semibold text-gray-700 break-all">DigitalLicenceStore@offersshopping.com</p>
                  </div>
                </a>
                <a
                  href="https://wa.me/79534940353"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex flex-col items-center gap-3 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100 rounded-xl p-5 hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl text-white group-hover:scale-110 transition-transform">
                    <i className="ri-whatsapp-line text-lg"></i>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400 mb-0.5">WhatsApp</p>
                    <p className="text-xs font-semibold text-gray-700">+7 953 494 0353</p>
                  </div>
                </a>
                <a
                  href="https://t.me/Sup0rteWin"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex flex-col items-center gap-3 bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-100 rounded-xl p-5 hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-sky-500 to-cyan-500 rounded-xl text-white group-hover:scale-110 transition-transform">
                    <i className="ri-telegram-line text-lg"></i>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400 mb-0.5">Telegram</p>
                    <p className="text-xs font-semibold text-gray-700">@Sup0rteWin</p>
                  </div>
                </a>
              </div>
            </article>

            {/* Key Points */}
            <section className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl p-6 sm:p-8 mt-10">
              <h3 className="text-lg font-bold text-gray-900 text-center mb-6">Puntos Importantes a Recordar</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-shield-star-line', color: 'teal', title: 'Protección de Datos', desc: 'Tu información está protegida con encriptación SSL de 256 bits' },
                  { icon: 'ri-scales-3-line', color: 'amber', title: 'Uso Justo', desc: 'Las licencias son para uso personal o comercial según el tipo' },
                  { icon: 'ri-refund-2-line', color: 'emerald', title: 'Garantía de Reembolso', desc: 'Reembolso completo si la licencia no funciona correctamente' },
                ].map((item, i) => (
                  <div key={i} className="text-center p-4 rounded-xl hover:bg-white hover:shadow-sm transition-all">
                    <div className={`w-12 h-12 flex items-center justify-center bg-${item.color}-100 rounded-xl mx-auto mb-3`}>
                      <i className={`${item.icon} text-${item.color}-600 text-xl`}></i>
                    </div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">{item.title}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
